/**
 * CdNameMismatchQueue — v0.8.5 (Bill 2026-05-15).
 *
 * Parallel queue to MissingOfficerCodeQueue. Raised by the admin-panel
 * SyncTab CD Reconcile → Apply path when Core Director's operator
 * descriptor (ParmValDesc) does not match v3 DisplayName (the
 * Entra-authoritative one). SysOps fixes the descriptor in CD out-of-band
 * (JHA Admin Console / Collette), then marks the row resolved here to
 * clear the flag.
 *
 * Backing endpoints:
 *   GET  /v3/admin/users/cd-name-mismatch                    — list source
 *   POST /v3/admin/users/{userId}/resolve-cd-name-mismatch   — clear flag
 */

import { useCallback, useEffect, useState } from 'react';
import { Loader2, ShieldCheck, AlertTriangle } from 'lucide-react';
import { useApiClient } from '@/contexts/ApiClientContext';
import type { CdNameMismatchRowV09 as CdNameMismatchRow } from '@retail-ops/api-client';

export function CdNameMismatchQueue(): JSX.Element {
  const api = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<CdNameMismatchRow> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cdFetchError, setCdFetchError] = useState<string | null>(null);
  const [resolvingId, setResolvingId] = useState<number | null>(null);
  const [savedToast, setSavedToast] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    setCdFetchError(null);
    try {
      const resp = await api.retail.admin.listCdNameMismatch();
      if (resp.success && Array.isArray(resp.data)) {
        setRows(resp.data);
        setCdFetchError(resp.cdFetchError ?? null);
      } else {
        setError('Failed to load queue.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onResolve = useCallback(async (row: CdNameMismatchRow): Promise<void> => {
    setResolvingId(row.userId);
    try {
      const resp = await api.retail.admin.resolveCdNameMismatch(row.userId);
      if (resp.success) {
        setSavedToast(`Cleared CdNameMismatch for ${row.displayName}.`);
        await reload();
      } else {
        setError(resp.error ?? `Resolve failed (${resp.code ?? 'unknown'}).`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setResolvingId(null);
    }
  }, [api, reload]);

  return (
    <>
      <h1>Core Director Operator Descriptor vs MS Entra Display Name Mismatch Queue</h1>
      <p className="page-intro">
        Active users whose Core Director operator descriptor (<code>ParmValDesc</code>)
        does not match the authoritative MS Entra Display Name. Entra wins, so the
        fix is to update the descriptor in Core Director. After correction, click{' '}
        <b>Mark Resolved</b> to clear the flag.
      </p>

      {savedToast && (
        <div className="banner banner--ok" role="status">
          <ShieldCheck size={16} className="banner__icon" />
          {savedToast}
        </div>
      )}
      {error && (
        <div className="banner banner--err" role="status">
          {error}
        </div>
      )}
      {cdFetchError && (
        <div className="banner banner--err" role="status">
          <AlertTriangle size={16} className="banner__icon" />
          <span>
            <strong>Core Director enrichment failed:</strong>{' '}
            <code>{cdFetchError}</code>
            <br />
            <span style={{ fontSize: '0.85em', opacity: 0.85 }}>
              CD descriptors will show <em>(catalog unreachable)</em> until
              this is resolved. The list itself is still authoritative — only
              the live <code>ParmValDesc</code> column is degraded.
            </span>
          </span>
        </div>
      )}

      {!rows && !error && (
        <div className="loading-inline">
          <Loader2 className="spin" size={16} /> Loading queue…
        </div>
      )}

      {rows && rows.length === 0 && (
        <div className="empty">
          <ShieldCheck size={32} className="empty__icon" />
          <p>No mismatch flags raised. ROP and CD descriptors are in agreement.</p>
        </div>
      )}

      {rows && rows.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>ROP DisplayName (authoritative)</th>
              <th>CD descriptor (live)</th>
              <th>Operator Initials</th>
              <th>Flagged</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.userId}>
                <td><strong>{u.email}</strong></td>
                <td>{u.displayName}</td>
                <td>
                  {u.cdDescriptor ? (
                    <span>{u.cdDescriptor}</span>
                  ) : (
                    <span className="cell-dim">— (catalog unreachable)</span>
                  )}
                </td>
                <td>
                  {u.officerCode ? (
                    <code className="cell-mono">{u.officerCode}</code>
                  ) : (
                    <span className="cell-dim">—</span>
                  )}
                </td>
                <td className="cell-mono">
                  {u.updatedAt ? new Date(u.updatedAt).toLocaleString() : <span className="cell-dim">—</span>}
                </td>
                <td className="ta-right">
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    onClick={() => void onResolve(u)}
                    disabled={resolvingId === u.userId}
                  >
                    <AlertTriangle size={14} />{' '}
                    {resolvingId === u.userId ? 'Clearing…' : 'Mark Resolved'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
